import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { ArrowRight, Sparkles, Shield, Building2 } from "lucide-react";

const Hero = () => {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden bg-slate-950">
      {/* Background gradient effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl" />
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          {/* Badge - Updated text */}
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-2 mb-8">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-medium">
              Trusted by local businesses nationwide
            </span>
          </div>

          {/* Beta Badge */}
          <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-4 py-2 mb-8 ml-2">
            <span className="text-yellow-400 text-sm font-medium">
              🎉 Beta — Free for a Limited Time!
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
            Leap Ahead of Your
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-600">
              Local Competition
            </span>
          </h1>

          {/* Subheading - Updated to focus on GBP */}
          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Get a comprehensive brand strategy with competitor insights, Google Business Profile analysis, 
            and actionable marketing campaigns—all generated by AI in minutes.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Button
              size="lg"
              onClick={() => navigate("/create")}
              data-testid="hero-cta-btn"
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-base font-medium shadow-lg shadow-emerald-500/25 transition-all hover:shadow-xl hover:shadow-emerald-500/30 hover:scale-105"
            >
              Generate My Free Strategy
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          {/* Trust indicators - Simplified */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Shield className="w-4 h-4 text-emerald-400" />
              <span>Your data is secure & private</span>
            </div>
            <div className="hidden sm:block w-1 h-1 bg-slate-600 rounded-full" />
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Building2 className="w-4 h-4 text-emerald-400" />
              <span>Built by <a href="http://vswagency.com" className="text-emerald-400 hover:underline" target="_blank" rel="noopener noreferrer">VSW Agency / Merge Publishing</a></span>
            </div>
          </div>

          {/* Feature highlights - Updated */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { icon: Sparkles, text: "AI-Generated Strategies" },
              { icon: Building2, text: "Local Brand Analysis" },
              { icon: Shield, text: "GBP Success Tips" },
            ].map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-center gap-3 bg-slate-900/50 border border-slate-800 rounded-xl px-6 py-4"
              >
                <item.icon className="w-5 h-5 text-emerald-400" />
                <span className="text-slate-300 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
